<template>
  <section
    v-bind="$attrs"
    :class="[display, align, gap]"
    class="bg-transparent border border-white/20 rounded-full px-4 py-1.5 hover:bg-primary-200/10 transition-colors duration-300 cursor-pointer group flex items-center"
    @click="usePopoverToggle(popover, $event)"
  >
    <div class="flex items-center gap-1.5 md:gap-2">
      <div
        class="hidden lg:block w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]"
      ></div>
      <div
        :class="[textColor, fontWeight, fontSize]"
        class="flex items-center gap-1.5 !text-[11px] lg:!text-sm !font-semibold"
      >
        <span class="hidden lg:inline-flex">
          {{ currentInstitution?.name }}
        </span>
        <span class="hidden lg:inline-flex text-white/40">—</span>
        <span class="uppercase tracking-wider lg:tracking-normal">
          {{ currentInstitution?.acronym }}
        </span>
      </div>
      <Icon icon="heroicons:chevron-down-20-solid" class="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
    </div>
  </section>

  <HealthPopover ref="popover" class="dark:bg-dark-800 bg-white max-w-sm" padding="!p-0">
    <ul class="max-h-64 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
      <li v-for="(institution, idx) in institutions" :key="idx">
        <button
          class="w-full flex items-center gap-2 justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          :class="{ 'bg-gray-100 dark:bg-gray-700': institution.id === currentInstitution?.id }"
          @click="selectInstitution(institution, $event)"
        >
          <section class="flex flex-col text-left">
            <p
              class="text-base leading-5 text-gray-800 dark:text-gray-200 font-medium"
              :class="{
                'font-semibold text-gray-900 dark:text-white':
                  institution.id === currentInstitution?.id,
              }"
            >
              {{ institution.name }}
            </p>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ institution.acronym }}
            </p>
          </section>

          <Icon
            v-if="institution.id === currentInstitution?.id"
            icon="mdi:check-circle"
            class="min-h-[1.5rem] min-w-[1.5rem] text-green-500"
          />
        </button>
      </li>
    </ul>
  </HealthPopover>
</template>

<script setup lang="ts">
  import { ref, computed } from "vue";
  import { useInstitution } from "@/shared/stores/institution";
  import { storeToRefs } from "pinia";
  import { usePopoverHide, usePopoverToggle } from "@/shared/composables/usePopover";
  import { Icon } from "@iconify/vue";
  import HealthPopover from "@/shared/components/utils/HealthPopover.vue";
  import { InstitutionModel } from "@/app/institution/models/InstitutionModel";

  const popover = ref();

  const institutionStore = useInstitution();

  const { institutions, institutionId } = storeToRefs(institutionStore);

  defineProps({
    display: {
      type: String,
      default: "flex",
    },
    align: {
      type: String,
      default: "items-center",
    },
    gap: {
      type: String,
      default: "gap-0 md:gap-1.5",
    },
    textColor: {
      type: String,
      default: "text-white",
    },
    fontWeight: {
      type: String,
      default: "font-medium",
    },
    fontSize: {
      type: String,
      default: "text-base",
    },
  });

  const currentInstitution = computed(() => {
    const id = institutionId.value;

    return institutions.value?.find((institution: InstitutionModel) => institution.id === id);
  });

  function selectInstitution(institution: InstitutionModel, event: Event) {
    institutionStore.setInstitutionId(Number(institution.id));

    usePopoverHide(popover.value, event);
  }
</script>
